import { combineEpics, ofType } from 'redux-observable';
import _get from 'lodash/get';
import { mergeMap, catchError } from 'rxjs/operators';
import { of, forkJoin, EMPTY } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { buildHeadersV2, buildAjaxCredentials } from 'helper/ajax-helper';
import geoEndpoints from 'actions/geo-endpoints';
import {
  enterpriseSelector,
  isPaxSelector,
  selectedCitySelector,
  userSafeIDSelector,
} from 'selectors/app-selector';
import GeoDuck from 'ducks/geo-duck';
import NewBookingDuck from 'ducks/new-booking-duck';
import { deliverySelector } from 'selectors/new-booking-selectors';
import {
  currentEditingPOISelector,
  poisFromSavedPOISelector,
  userLocationSelector,
} from 'selectors/geo-selectors';
import { getPickUpPOIForUser } from 'helper/storage';
import {
  fetchV3FavoritesPOIs,
  fetchV3PredictPOIs,
} from 'helper/saved-poi-helper';
import { getCityRefCoordinates } from 'helper/address-search-helpers';
import { pushNotificationAction } from 'actions/app-actions';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';
import I18n from 'i18n-js';
import _isEmpty from 'lodash/isEmpty';
import { injectUuidToPOI, injectUuidToPOIs } from '../../helper/geo-helper';

const mapSearchEpic = (action$, state$) =>
  action$.pipe(
    ofType(GeoDuck.types.MAP_SEARCH),
    mergeMap(action => {
      const { latitude, longitude, pickupOrDropoff } = action;

      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      return forkJoin(
        of(action),
        ajax({
          url: geoEndpoints.v3ReverseGeoEndpoint({
            latitude,
            longitude,
            pickupOrDropoff,
            isToggle: true,
          }),
          method: 'GET',
          headers: buildHeadersV2({
            isPax,
            companyID: _get(enterprise, 'companyID'),
            includeInternalHeaders: true,
          }),
          withCredentials: buildAjaxCredentials(isPax),
          timeout: 3000,
        }).pipe(catchError(error => of({ error }))),
      );
    }),
    mergeMap(([action, { response, error }]) => {
      const { latitude, longitude, pickupOrDropoff, callback } = action;

      if (error || !response) {
        callback(undefined, error, { latitude, longitude, pickupOrDropoff });
        return EMPTY;
      }

      callback(response.response, undefined, {
        latitude,
        longitude,
        pickupOrDropoff,
      });
      return EMPTY;
    }),
  );

const fetchSuggestPOIsEpic = (action$, state$) =>
  action$.pipe(
    ofType(GeoDuck.types.FETCH_SUGGEST_POIS),
    mergeMap(action => {
      const { pickupOrDropoff } = action;
      const city = selectedCitySelector(state$.value);
      const coords = getCityRefCoordinates(city, true);
      const latitude = _get(coords, 'latitude');
      const longitude = _get(coords, 'longitude');
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const isPickUp = pickupOrDropoff === 'pickup';
      return forkJoin(
        ajax({
          url: geoEndpoints.v3SuggestPOIEndpoint({
            latitude,
            longitude,
            pickupOrDropoff,
            isToggle: true,
          }),
          method: 'GET',
          headers: buildHeadersV2({
            isPax,
            companyID: _get(enterprise, 'companyID'),
            includeInternalHeaders: true,
          }),
          withCredentials: buildAjaxCredentials(isPax),
          timeout: 3000,
        }).pipe(catchError(error => of({ error }))),
        of(isPickUp),
      );
    }),
    mergeMap(([{ response, error }, isPickUp]) => {
      if (error || !response) {
        return EMPTY;
      }
      const resp = _get(response, 'response');
      const uuid = _get(resp, 'uuid');
      injectUuidToPOIs(_get(resp, 'result'), uuid);
      injectUuidToPOIs(_get(resp, 'extra'), uuid);
      return of(GeoDuck.creators.setSuggestPOIs(resp, isPickUp));
    }),
  );

const fetchLastPickUpStepEpic = (action$, state$) =>
  action$.pipe(
    ofType(GeoDuck.types.FETCH_DEFAULT_PICKUP_STEP),
    mergeMap(action => {
      const { deliveryID } = action;
      const delivery = deliverySelector(state$.value, { deliveryID });
      if (_get(delivery, ['detail', 'pickUp', 'poi', 'id'])) {
        return EMPTY;
      }

      let latitude, longitude;

      // try with last pick up POI
      const city = selectedCitySelector(state$.value);
      const safeID = userSafeIDSelector(state$.value);
      const cityID = _get(city, 'cityID');
      const lastPickUpPOI = getPickUpPOIForUser(cityID, safeID);
      latitude = _get(lastPickUpPOI, ['latlng', 'latitude']);
      longitude = _get(lastPickUpPOI, ['latlng', 'longitude']);

      // try with user location
      if (!latitude || !longitude) {
        const userLocation = userLocationSelector(state$.value);
        latitude = _get(userLocation, 'latitude');
        longitude = _get(userLocation, 'longitude');
      }

      if (!latitude || !longitude) {
        return EMPTY;
      }

      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      return forkJoin(
        of(deliveryID),
        of(cityID),
        of(lastPickUpPOI),
        ajax({
          url: geoEndpoints.v3ReverseGeoEndpoint({
            latitude,
            longitude,
            pickupOrDropoff: 'pickup',
            isToggle: true,
          }),
          method: 'GET',
          headers: buildHeadersV2({
            isPax,
            companyID: _get(enterprise, 'companyID'),
            includeInternalHeaders: true,
          }),
          withCredentials: buildAjaxCredentials(isPax),
          timeout: 3000,
        }).pipe(catchError(error => of({ error }))),
      );
    }),
    mergeMap(([deliveryID, cityID, lastPickUpPOI, { response, error }]) => {
      if (error || !response) {
        return EMPTY;
      }
      const poi = _get(response, 'response.result');
      const uuid = _get(response, 'response.uuid');
      injectUuidToPOI(poi, uuid);

      if (lastPickUpPOI) {
        // resolve from last pickup poi
        const lastPickUpPOIID = _get(lastPickUpPOI, 'id');
        const poiID = _get(poi, 'id');
        if (poiID && lastPickUpPOIID && poiID === lastPickUpPOIID) {
          return of(
            NewBookingDuck.creators.setPickUp(
              deliveryID,
              { poi },
              'LAST_SENDER_POI',
            ),
          );
        }
      } else {
        // resolve from user location
        const poiCityID = _get(poi, 'address.cityId');
        if (cityID === poiCityID) {
          return of(
            NewBookingDuck.creators.setPickUp(
              deliveryID,
              { poi },
              'CURRENT_LOCATION',
            ),
          );
        }
      }

      return EMPTY;
    }),
  );

const fetchSavedPOIsEpic = (action$, state$) =>
  action$.pipe(
    ofType(GeoDuck.types.FETCH_SAVED_POIS),
    mergeMap(action => {
      const city = selectedCitySelector(state$.value);
      const coords = getCityRefCoordinates(city, true);
      const latitude = _get(coords, 'latitude');
      const longitude = _get(coords, 'longitude');

      const isPax = isPaxSelector(state$.value);
      const company = enterpriseSelector(state$.value);
      const obs = [];
      obs.push(fetchV3FavoritesPOIs({ latitude, longitude, isPax, company }));
      obs.push(fetchV3PredictPOIs({ latitude, longitude, isPax, company }));
      return forkJoin(...obs);
    }),
    mergeMap(([favoriteResponse, predictResponse]) => {
      const favoriteUuid = _get(favoriteResponse, ['response', 'uuid'], '');
      const favoritePOIs = _get(favoriteResponse, ['response', 'result'], []);
      injectUuidToPOIs(favoritePOIs, favoriteUuid);
      const predictUuid = _get(predictResponse, ['response', 'uuid'], '');
      const predictPOIs = _get(predictResponse, ['response', 'result'], []);
      injectUuidToPOIs(predictPOIs, predictUuid);
      return of(
        GeoDuck.creators.setFavoritePOIs(favoritePOIs),
        GeoDuck.creators.setPredictPOIs(predictPOIs),
      );
    }),
  );

const saveFavoritePOIEpic = (action$, state$) =>
  action$.pipe(
    ofType(GeoDuck.types.SAVE_FAVORITE_POI),
    mergeMap(action => {
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const { currentPOI, originalPOI } = currentEditingPOISelector(
        state$.value,
      );
      const favoritesPOIs = poisFromSavedPOISelector(state$.value);

      let oldPoiID = _get(originalPOI, 'id');

      const currentPOILabel = _get(currentPOI, 'label');
      const currentPOIID = _get(currentPOI, 'id');
      if (!_isEmpty(favoritesPOIs) && currentPOILabel) {
        const existingPOIWithSameLabel = favoritesPOIs.find(
          poi => poi.label === currentPOILabel && poi.id !== currentPOIID,
        );
        if (existingPOIWithSameLabel) {
          oldPoiID = existingPOIWithSameLabel.id;
        }
      }

      const payload = {
        ts: 0,
        oldPoiID,
        pois: [currentPOI],
      };

      return ajax({
        url: geoEndpoints.v3SaveFavoritesPOIsEndpoint(),
        method: 'POST',
        headers: buildHeadersV2({
          isPax,
          companyID: _get(enterprise, 'companyID'),
          includeInternalHeaders: true,
        }),
        withCredentials: buildAjaxCredentials(isPax),
        body: JSON.stringify(payload),
        timeout: 3000,
      }).pipe(catchError(error => of({ error })));
    }),
    mergeMap(({ response }) => {
      const obs = [];
      const result = _get(response, 'response');
      if (result && result.status !== 'err_saved_place_label') {
        // Send toast notification
        obs.push(
          pushNotificationAction({
            id: 'success-save-poi',
            type: NOTIFICATION_TYPE.CONFIRMED,
            message: I18n.t('v2.savedPOI.notification.successSavePOI.title'),
          }),
        );
      } else {
        // Send failed notification
        obs.push(
          pushNotificationAction({
            id: 'success-save-poi',
            type: NOTIFICATION_TYPE.WARNING,
            message: I18n.t('v2.savedPOI.notification.failedSavedPOI.title'),
          }),
        );
      }
      obs.push(GeoDuck.creators.fetchSavedPOIs());
      obs.push(GeoDuck.creators.fetchSuggestPOIs('dropoff'));
      return of(...obs);
    }),
  );

const deleteFavoritePOIEpic = (action$, state$) =>
  action$.pipe(
    ofType(GeoDuck.types.DELETE_FAVORITE_POI),
    mergeMap(action => {
      const city = selectedCitySelector(state$.value);
      let latitude, longitude;
      if (city) {
        const coords = getCityRefCoordinates(city);
        latitude = _get(coords, 'latitude');
        longitude = _get(coords, 'longitude');
      }
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const { currentPOI } = currentEditingPOISelector(state$.value);
      const payload = {
        latitude,
        longitude,
        poiIDs: _get(currentPOI, 'id'),
      };
      return ajax({
        url: geoEndpoints.v3SaveFavoritesPOIsEndpoint(),
        method: 'DELETE',
        headers: buildHeadersV2({
          isPax,
          companyID: _get(enterprise, 'companyID'),
          includeInternalHeaders: true,
        }),
        withCredentials: buildAjaxCredentials(isPax),
        body: JSON.stringify(payload),
        timeout: 3000,
      }).pipe(catchError(error => of({ error })));
    }),
    mergeMap(({ response }) => {
      const obs = [];
      const result = _get(response, 'response');
      if (result) {
        // Send toast notification
        obs.push(
          pushNotificationAction({
            id: 'success-save-poi',
            type: NOTIFICATION_TYPE.CONFIRMED,
            message: I18n.t('v2.savedPOI.notification.successDeletePOI.title'),
          }),
        );
      }
      // Re-fetch saved POI
      obs.push(GeoDuck.creators.fetchSavedPOIs());
      return of(...obs);
    }),
  );

export default combineEpics(
  mapSearchEpic,
  fetchLastPickUpStepEpic,
  fetchSuggestPOIsEpic,
  fetchSavedPOIsEpic,
  saveFavoritePOIEpic,
  deleteFavoritePOIEpic,
);
